import { useContext, useEffect, useState } from "react";
import { Box, Select, MenuItem, Tab, Tabs, InputLabel, Button } from '@mui/material';
import { useLocation } from 'react-router-dom'

import TabFinancialReports 	from "./TabFinancialReports";
import TabGeneral 			from "./TabGeneral";
import TabDividends 		from "./TabDividends";
import TabIndicators		from "./TabIndicators";
import TabCharts 			from "./TabCharts";
import TabNotes 			from "./TabNotes";
import TabForum 			from "./TabForum";
import TabValuation 		from "./TabValuation";
import TabUserParameters from "./TabUserParameters";

import CompanyContext 		from "../context";
import useFetch 			from "../../../api/api";
import useBreakpoint 		from "../../../hooks/useBreakpoint";

import PriceCheck 			from '@mui/icons-material/PriceCheck';
import AssignmentIcon 		from '@mui/icons-material/Assignment';
import PaidIcon				from '@mui/icons-material/Paid';
import AssessmentIcon 		from '@mui/icons-material/Assessment';
import SpeedIcon 			from '@mui/icons-material/Speed';
import SsidChartIcon 		from '@mui/icons-material/SsidChart';
import EditNoteIcon 		from '@mui/icons-material/EditNote';
import ForumIcon			from '@mui/icons-material/Forum';
import PersonIcon			from "@mui/icons-material/Person";

import config from "../../../common/Config";
import useAuth from "../../../hooks/useAuth";

const PanelMain = () => {

	const {auth}										= useAuth()

	const { stocks, selectedStock, setSelectedStock, setPortfolioVersion }	= useContext(CompanyContext)

	const [currentTabIndex, setCurrentTabIndex] 		= useState(0);
	const [isInPortfolio, setIsInPortfolio]				= useState(false)
	const [isLogged, setIsLogged]						= useState(auth.userId ? true : false)

	const privateFetch									= useFetch()
	const {hash}										= useLocation()
	const currentBreakPoint								= useBreakpoint()

	useEffect(() => {
		setIsLogged(auth.userId ? true : false)
	}, [auth])

	// Verifica se a empresa esta no portfolio e, se sim, traz seus dados
	useEffect(() => {

		// Se está logado, verifica se está no portfolio do usuario
		if (isLogged) {
			privateFetch(config.apiUrl +'/api/portfolio')
			.then(result => result.json())
			.then(result => {
				result.find(p => p.ticker === selectedStock) ? setIsInPortfolio(true) : setIsInPortfolio(false)
			})
		}		

		switch(hash) {
			case '#dividends' 		: setCurrentTabIndex(2); break
			case '#financialData' 	: setCurrentTabIndex(1); break
		}
	}, [selectedStock])

	const handleTabChange = (e, tabIndex) => {
		setCurrentTabIndex(tabIndex);
	};

	const handleAddToPortfolio = () => {
		let options = {
			method : "POST",
			headers : {
				"Content-Type" : "application/json"	
			},
			body : `{ 
				"ticker" 	: "`+ selectedStock +`", 
				"assetType"	: "`+ (stocks.find(s => s.ticker === selectedStock)?.market === 'BR' ? 'ACAO' : 'STOCK') + `",
				"target" 	: "0"
			}`			
		}
		
		privateFetch(config.apiUrl +"/api/portfolio/addTarget", options)
		.then(() => { setIsInPortfolio(true); setPortfolioVersion(v => v + 1) });
	}

	const handleRemoveFromPortfolio = () => {
		let options = {
			method : "DELETE",
			headers : {
				"Content-Type" : "application/json"
			}
		}
		privateFetch(config.apiUrl +"/api/portfolio/"+ selectedStock, options)
		.then(() => { setIsInPortfolio(false); setPortfolioVersion(v => v + 1) });
	}

	return (
		<Box sx={{width: '100%', display: 'block'}}>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }} display={'flex'} alignItems={'center'} justifyContent={'right'}>
				{currentBreakPoint('SIZE') > 3 ? (
					<InputLabel sx={{paddingRight:1}}>Ticker:</InputLabel>
				) : null }
				<Select label='Selecione' value={selectedStock || ""} sx={{height:40, width:100}} onChange={(e) => setSelectedStock(e.target.value)}>
					<MenuItem key="" value="" disabled>Selecione o ticker</MenuItem>
					{stocks && stocks.map(s => 
						<MenuItem key={s.ticker} value={s.ticker}>{s.ticker}</MenuItem>
					)}
				</Select>

				{currentBreakPoint('SIZE') > 2 ? (
					<TabsForDesktop value={currentTabIndex} onChange={handleTabChange}/>
				) : (
					<TabsForMobile value={currentTabIndex} onChange={handleTabChange} isLogged={isLogged}/>
				)}

				<Box display={'flex'} flex={1} alignContent={'center'} justifyContent={'right'} marginRight={1}>
					{ isInPortfolio ? (
						<Button variant="contained" color="secondary" sx={{width: 90}} onClick={handleRemoveFromPortfolio}>- Seguir</Button>
					) : (
						<Button variant="contained" sx={{width: 90}} onClick={handleAddToPortfolio} disabled={!isLogged}>+ Seguir</Button>
					)}
				</Box>
				
			</Box>

			<Box sx={{marginTop:2, width: '98%'}}>
				{ currentTabIndex === 0 ? (<TabGeneral/>) : null }
				{ currentTabIndex === 1 ? (<TabFinancialReports/>) : null }
				{ currentTabIndex === 2 ? (<TabDividends/>) : null }
				{ currentTabIndex === 3 ? (<TabIndicators/>) : null }
				{ currentTabIndex === 4 ? (<TabValuation/>) : null }
				{ currentTabIndex === 5 ? (<TabCharts/>) : null }
				{ currentTabIndex === 6 ? (<TabNotes/>) : null }
				{ currentTabIndex === 7 ? (<TabForum/>) : null }
				{ currentTabIndex === 8 ? (<TabUserParameters/>) : null }
			</Box>
		</Box>
	)
}

const TabsForDesktop = ({value, onChange}) => {
	return (
		<Tabs value={value} onChange={onChange} variant="scrollable" allowScrollButtonsMobile  scrollButtons="auto">					
			<Tab key="0" label="Geral" />
			<Tab key="1" label="Dados Financeiros" />
			<Tab key="2" label="Dividendos" />
			<Tab key="3" label="Indicadores" />
			<Tab key="4" label="Valuation" />
			<Tab key="5" label="Gráficos" />
		</Tabs>
	)
}

const TabsForMobile = ({value, onChange, isLogged}) => {
	return (
		<Tabs value={value} onChange={onChange} variant="scrollable" allowScrollButtonsMobile  scrollButtons="auto">					
			<Tab key="0" icon={<AssignmentIcon/>} sx={{minWidth: 50}}/>
			<Tab key="1" icon={<AssessmentIcon/>} sx={{minWidth: 50}}/>
			<Tab key="2" icon={<PaidIcon/>} sx={{minWidth: 50}}/>
			<Tab key="3" icon={<SpeedIcon/>} sx={{minWidth: 50}}/>
			<Tab key="4" icon={<PriceCheck/>} sx={{minWidth: 50}}/>
			<Tab key="5" icon={<SsidChartIcon/>} sx={{minWidth: 50}} disabled={!isLogged}/>
			<Tab key="6" icon={<EditNoteIcon/>} sx={{minWidth: 50}} disabled={!isLogged}/>
			<Tab key="7" icon={<ForumIcon/>} sx={{minWidth: 50}} disabled={!isLogged}/>
			<Tab key="8" icon={<PersonIcon/>} sx={{minWidth: 50}} disabled={!isLogged}/>
		</Tabs>
	)
}

export default PanelMain